import MiniSearch from "minisearch";
import { db } from "./db";
import type { Document } from "@/types";

interface SearchDoc extends Record<string, unknown> {
  id: string;
  canonicalName: string;
  originalNames: string[];
  mimeType: string;
  extension: string;
  senderEmails: string;
  subjects: string;
}

let miniSearch: MiniSearch<SearchDoc> | null = null;

function docToSearchDoc(doc: Document, senderEmails: string, subjects: string): SearchDoc {
  return {
    id: doc.id,
    canonicalName: doc.canonicalName,
    originalNames: doc.originalNames,
    mimeType: doc.mimeType,
    extension: doc.extension,
    senderEmails,
    subjects,
  };
}

export async function buildSearchIndex(): Promise<MiniSearch<SearchDoc>> {
  const docs = await db.documents.toArray();
  const occurrences = await db.occurrences.toArray();

  const enriched = docs.map((doc) => {
    const occs = occurrences.filter((o) => o.documentId === doc.id);
    const senders = [...new Set(occs.map((o) => o.senderEmail).filter(Boolean))].join(" ");
    const subjects = [...new Set(occs.map((o) => o.subject).filter(Boolean))].join(" ");
    return docToSearchDoc(doc, senders, subjects);
  });

  const ms = new MiniSearch<SearchDoc>({
    fields: ["canonicalName", "originalNames", "mimeType", "extension", "senderEmails", "subjects"],
    storeFields: ["id", "canonicalName", "mimeType", "extension", "sizeBytes", "createdAt"],
    searchOptions: {
      boost: { canonicalName: 2, originalNames: 1.5 },
      fuzzy: 0.2,
      prefix: true,
    },
  });

  ms.addAll(enriched);
  miniSearch = ms;
  return ms;
}

export async function getSearchIndex(): Promise<MiniSearch<SearchDoc>> {
  if (miniSearch) return miniSearch;
  return buildSearchIndex();
}

export async function searchDocuments(query: string): Promise<Document[]> {
  if (!query.trim()) {
    return db.documents.toArray();
  }
  const ms = await getSearchIndex();
  const results = ms.search(query);
  const ids = results.map((r) => r.id);
  const docs = await db.documents.bulkGet(ids);
  return docs.filter((d): d is Document => !!d);
}

export async function addToIndex(doc: Document): Promise<void> {
  const ms = await getSearchIndex();
  const occs = await db.occurrences.where({ documentId: doc.id }).toArray();
  const senders = [...new Set(occs.map((o) => o.senderEmail).filter(Boolean))].join(" ");
  const subjects = [...new Set(occs.map((o) => o.subject).filter(Boolean))].join(" ");
  ms.add(docToSearchDoc(doc, senders, subjects));
}

export async function removeFromIndex(docId: string): Promise<void> {
  const ms = await getSearchIndex();
  ms.discard(docId);
}

export function clearSearchIndex(): void {
  miniSearch = null;
}
